<template>
  <div>
    <div class="print-container">

        <div class="header-row">
          <h2 class="title">
            {{ isRaw ? '원자재 발주서' : '부자재 발주서' }}
            <!-- 상태 관리 도입 (2026-07-15) -->
            <el-tag :type="statusTagType" size="large" style="margin-left:10px">
              {{ order.status_display }}
            </el-tag>
            <span v-if="order.approval_status === 'PENDING'" class="approval-hint">
              결재 진행중 — 승인 시 자동 발주완료
            </span>
            <div class="doc-no">
              {{ order.document_number }}
              <!-- 발주↔지출결의 연결 (2026-07-15): 이 발주의 입고가 담긴 지결번호 -->
              <template v-if="order.expense_refs?.length">
                <el-tag
                  v-for="e in order.expense_refs" :key="e.id"
                  size="small" type="warning" class="exp-ref"
                  :title="`마감월 ${e.close_month || '-'} · 상태 ${e.status}`"
                >지결 {{ e.document_number }}</el-tag>
              </template>
            </div>
          </h2>
          <table class="approval-table print-only">
            <tbody>
              <tr>
                <th rowspan="2">결재</th>
                <th>담당</th>
                <th>주임</th>
                <th>대리</th>
                <th>팀장</th>
                <th>실장</th>
                <th>이사</th>
              </tr>
              <tr>
                <td class="sign-cell" v-for="i in 6" :key="i">&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="order-info">
          <table class="info-table">
            <tbody>
              <tr>
                <td class="label-cell">공급사</td><td>{{ order.supplier_name }}</td>
                <td class="label-cell">브랜드</td><td>{{ order.brand_name }}</td>
              </tr>
              <tr>
                <td class="label-cell">발주일</td><td>{{ formatDate(order.order_date) }}</td>
                <td class="label-cell">납기일</td><td>{{ formatDate(order.delivery_date) }}</td>
              </tr>
              <tr>
                <td class="label-cell">스타일번호</td><td>{{ order.main_style_number }}</td>
                <td class="label-cell">기획수량</td><td>{{ order.request_quantity }}</td>
              </tr>
              <tr>
                <td class="label-cell">발주담당</td><td>{{ order.order_manager_name }}</td>
                <td class="label-cell">입고처</td><td>{{ order.receiving_location }}</td>
              </tr>
              <tr v-if="order.main_request_number">
                <td class="label-cell">메인오더</td>
                <td colspan="3">
                  <el-link type="primary" @click="$router.push(`/main-requests/${order.main_request}`)">
                    {{ order.main_request_number }}
                  </el-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 입고 진행률 요약 -->
        <div v-if="items.length > 0" class="receiving-summary">
          <div class="receiving-summary-header">
            <span>입고 진행률</span>
            <span class="receiving-summary-value">
              {{ receivedItemCount }} / {{ items.length }} 품목 완료
            </span>
          </div>
          <el-progress
            :percentage="overallReceivingRate"
            :color="overallReceivingRate >= 100 ? '#67C23A' : '#409EFF'"
            :stroke-width="12"
          />
        </div>

        <el-tabs v-model="activeTab" class="mt-4">
          <el-tab-pane label="자재목록" name="items">
            <div class="metrial-info">

              <!-- 원자재 -->
              <template v-if="isRaw && items.length > 0">
                <table class="item-table">
                  <tbody>
                    <tr>
                      <td colspan="6" class="item-name-row">
                        <strong>품명:</strong> {{ items[0].name }} &nbsp;&nbsp;
                        <strong>규격:</strong> {{ items[0].specification }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table class="item-table">
                  <thead>
                    <tr>
                      <th>견본</th>
                      <th>Color</th>
                      <th>발주량</th>
                      <th>입고량</th>
                      <th>패킹</th>
                      <th>수출</th>
                      <th>진행률</th>
                      <th>비고</th>
                      <th class="no-print">입고</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in items" :key="idx">
                      <td class="swatch-cell">샘플 부착</td>
                      <td class="small-cell">{{ item.color }}</td>
                      <td class="small-cell">{{ item.order_quantity }}</td>
                      <td class="small-cell">{{ item.received_quantity || 0 }}</td>
                      <td class="small-cell">{{ item.packed_quantity || 0 }}</td>
                      <td class="small-cell">{{ item.exported_quantity || 0 }}</td>
                      <td class="small-cell">
                        <el-progress
                          :percentage="getItemReceivingRate(item)"
                          :stroke-width="6"
                          :show-text="false"
                          :color="getItemReceivingRate(item) >= 100 ? '#67C23A' : '#409EFF'"
                          style="width: 60px; display: inline-block"
                        />
                        <span class="progress-text">{{ getItemReceivingRate(item) }}%</span>
                      </td>
                      <td class="small-cell">{{ item.remarks }}</td>
                      <td class="no-print">
                        <!-- 입고는 발주 확정 후에만 (2026-07-15 상태 흐름) -->
                        <el-button
                          v-if="['ORDERED', 'PARTIAL_RECEIVED', 'FULLY_RECEIVED'].includes(order.status)"
                          type="primary" size="small" @click="openReceiving(item)"
                        >입고</el-button>
                        <span v-else class="no-receive-hint">확정 후 입고</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>

              <!-- 부자재 -->
              <template v-else-if="items.length > 0">
                <table class="item-table">
                  <thead>
                    <tr>
                      <th>COLOR</th>
                      <th>품명</th>
                      <th>규격</th>
                      <th>요척</th>
                      <th>발주량</th>
                      <th>단가</th>
                      <th>금액</th>
                      <th>입고량</th>
                      <th>패킹</th>
                      <th>수출</th>
                      <th>진행률</th>
                      <th>비고</th>
                      <th class="no-print">입고</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in items" :key="idx">
                      <td>{{ item.color }}</td>
                      <td>{{ item.name }}</td>
                      <td>{{ item.specification }}</td>
                      <td>{{ item.required_quantity }}</td>
                      <td>{{ formatPrice(item.order_quantity) }}</td>
                      <td>{{ formatPrice(item.unit_price) }}</td>
                      <td>{{ formatPrice(item.total_price) }}</td>
                      <td>{{ item.received_quantity || 0 }}</td>
                      <td>{{ item.packed_quantity || 0 }}</td>
                      <td>{{ item.exported_quantity || 0 }}</td>
                      <td>
                        <el-progress
                          :percentage="getItemReceivingRate(item)"
                          :stroke-width="6"
                          :show-text="false"
                          :color="getItemReceivingRate(item) >= 100 ? '#67C23A' : '#409EFF'"
                          style="width: 60px; display: inline-block"
                        />
                        <span class="progress-text">{{ getItemReceivingRate(item) }}%</span>
                      </td>
                      <td>{{ item.remarks }}</td>
                      <td class="no-print">
                        <!-- 입고는 발주 확정 후에만 (2026-07-15 상태 흐름) -->
                        <el-button
                          v-if="['ORDERED', 'PARTIAL_RECEIVED', 'FULLY_RECEIVED'].includes(order.status)"
                          type="primary" size="small" @click="openReceiving(item)"
                        >입고</el-button>
                        <span v-else class="no-receive-hint">확정 후 입고</span>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td class="right-cell" colspan="6">합계</td>
                      <td>{{ formatPrice(totalPrice) }} 원</td>
                      <td colspan="4">VAT 별도</td>
                    </tr>
                  </tfoot>
                </table>
              </template>

              <el-empty v-else description="등록된 자재 항목이 없습니다." />

            </div>
          </el-tab-pane>
          <el-tab-pane label="입고내역" name="receivings">
            <el-table :data="receivings || []" style="width: 100%">
              <el-table-column prop="item_color" label="색상" />
              <el-table-column prop="item_name" label="품명" />
              <el-table-column prop="item_specification" label="규격" />
              <el-table-column prop="quantity" label="입고 수량" :formatter="(row) => formatPrice(row.quantity)" />
              <el-table-column prop="received_date" label="입고일" :formatter="(row) => formatDate(row.received_date)" />
              <el-table-column prop="receiving_type_display" label="입고유형" width="100" />
              <el-table-column prop="warehouse_name" label="입고창고" />
              <el-table-column prop="receiver_name" label="입고자" />
              <el-table-column prop="remarks" label="비고" />
            </el-table>
          </el-tab-pane>
        </el-tabs>
    </div>

    <!-- 상태별 액션 (2026-07-15): 작성중=수정/상신/확정, 확정 후=취소(이력 없을 때)만 -->
    <div class="button-group">
        <template v-if="order.status === 'DRAFT' && order.approval_status !== 'PENDING'">
          <el-button type="primary" @click="editOrder">수정</el-button>
          <el-button type="success" :loading="acting" @click="submitApproval">결재 상신</el-button>
          <el-button type="warning" plain :loading="acting" @click="confirmOrder">발주 확정</el-button>
        </template>
        <el-button
          v-if="['ORDERED', 'PARTIAL_RECEIVED'].includes(order.status)"
          type="danger" plain :loading="acting" @click="cancelOrder"
        >발주 취소</el-button>
        <el-button @click="goBack">뒤로가기</el-button>
        <el-button :loading="pdfLoading" @click="openPdf">PDF 출력</el-button>
    </div>
  </div>

  <ReceivingFormDialog ref="receivingDialog" @submitted="fetchOrder" />

  <!-- 결재선 지정 상신 (2026-07-15) — 견본실과 동일 다이얼로그:
       기본 결재선(템플릿/조직도) 로드 후 상신자가 승인/합의/참조 편집 -->
  <ApprovalSubmitDialog
    v-model="showSubmitDialog"
    document-type="material_order"
    :document-id="route.params.id"
    document-api-path="material-orders"
    @submitted="onApprovalSubmitted"
  />

</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import ReceivingFormDialog from '@/components/ReceivingFormDialog.vue'
import ApprovalSubmitDialog from '@/components/approval/ApprovalSubmitDialog.vue'

const route = useRoute()
const router = useRouter()
const order = ref({
  id: null,
  material_type: '',
  brand_name: '',
  supplier_name: '',
  main_style_number: '',
  delivery_date: '',
  order_date: '',
  order_manager_name: '',
  order_manager_contact: '',
  receiving_location: '',
  designer_name: '',
  request_quantity: 0,
  additional_notes: '',
  items: [],
})
const items = computed(() => order.value.items || [])

onMounted(async () => {
  await fetchOrder()
})

const fetchOrder = async () => {
  const res = await axiosInstance.get(`/material-orders/${route.params.id}/`)
  const data = res.data.results || res.data
  order.value = { ...data, items: data.items || [] }
}

const isRaw = computed(() => order.value.material_type === 'raw')

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('ko-KR')
}

const totalPrice = computed(() => {
  return order.value.items.reduce((sum, item) => {
    return sum + Number(item.total_price || 0)
  }, 0)
})

const formatPrice = (value) => {
  const number = Number(value || 0)
  return number.toLocaleString('ko-KR')
}

// 입고 진행률 계산
const getItemReceivingRate = (item) => {
  const ordered = Number(item.order_quantity) || 0
  const received = Number(item.received_quantity) || 0
  if (ordered <= 0) return 0
  return Math.min(Math.round((received / ordered) * 100), 100)
}

const receivedItemCount = computed(() => {
  return items.value.filter(item => {
    const ordered = Number(item.order_quantity) || 0
    const received = Number(item.received_quantity) || 0
    return ordered > 0 && received >= ordered
  }).length
})

const overallReceivingRate = computed(() => {
  const totalOrdered = items.value.reduce((s, i) => s + (Number(i.order_quantity) || 0), 0)
  const totalReceived = items.value.reduce((s, i) => s + (Number(i.received_quantity) || 0), 0)
  if (totalOrdered <= 0) return 0
  return Math.min(Math.round((totalReceived / totalOrdered) * 100), 100)
})

// 입고 처리
const receivingDialog = ref(null)

const openReceiving = (item) => {
  receivingDialog.value.open(item)
}

const activeTab = ref('items')
const receivings = ref([])

watch(activeTab, async (tab) => {
  if (tab === 'receivings') {
    const res = await axiosInstance.get(`/receivings/?material_order=${route.params.id}`)
    receivings.value = res.data.results || res.data
  }
})

const goBack = () => history.back()
const editOrder = () => router.push(`/material-orders/${route.params.id}/edit`)

// ===== 상태 액션 (2026-07-15) =====
const acting = ref(false)

const statusTagType = computed(() => ({
  DRAFT: 'info',
  ORDERED: 'primary',
  PARTIAL_RECEIVED: 'warning',
  FULLY_RECEIVED: 'success',
  CANCELLED: 'danger',
}[order.value.status] || 'info'))

const extractDetail = (e, fallback) =>
  e.response?.data?.detail || e.response?.data?.error || fallback

// 결재선 지정 다이얼로그로 상신 (2026-07-15) — 작성자가 누구에게
// 올라가는지 보고 결재선을 편집한 뒤 상신
const showSubmitDialog = ref(false)
const submitApproval = () => {
  showSubmitDialog.value = true
}
const onApprovalSubmitted = async () => {
  await fetchOrder()
  if (order.value.status === 'ORDERED') {
    ElMessage.success('결재가 완료되어 발주완료 처리되었습니다.')
  } else {
    ElMessage.info('결재 진행 중에는 수정할 수 없습니다. 승인 완료 시 자동으로 발주완료 처리됩니다.')
  }
}

const confirmOrder = async () => {
  try {
    await ElMessageBox.confirm(
      '결재 없이 발주를 확정합니다. 확정 후에는 수정할 수 없습니다.',
      '발주 확정', { confirmButtonText: '확정', cancelButtonText: '취소', type: 'warning' }
    )
  } catch { return }
  acting.value = true
  try {
    const res = await axiosInstance.post(`/material-orders/${route.params.id}/confirm-order/`)
    ElMessage.success(res.data.message)
    await fetchOrder()
  } catch (e) {
    ElMessage.error(extractDetail(e, '발주 확정에 실패했습니다.'))
  } finally {
    acting.value = false
  }
}

const cancelOrder = async () => {
  try {
    await ElMessageBox.confirm(
      '발주를 취소합니다. 입고/출고/패킹 이력이 있으면 취소할 수 없습니다.',
      '발주 취소', { confirmButtonText: '발주 취소', cancelButtonText: '닫기', type: 'warning' }
    )
  } catch { return }
  acting.value = true
  try {
    const res = await axiosInstance.post(`/material-orders/${route.params.id}/cancel-order/`)
    ElMessage.success(res.data.message)
    await fetchOrder()
  } catch (e) {
    ElMessage.error(extractDetail(e, '발주 취소에 실패했습니다.'))
  } finally {
    acting.value = false
  }
}

// 서버 PDF 일원화 (2026-07-15) — 종이 인쇄·이메일 첨부가 항상 같은 문서.
// 기존 팝업 인쇄 화면(/material-orders/:id/print)은 사용 중단
// (합계 0원·VAT 표기 모순 등 문서 결함으로 공식 문서 부적합 판정).
const pdfLoading = ref(false)
const openPdf = async () => {
  pdfLoading.value = true
  try {
    const res = await axiosInstance.get(
      `/material-orders/${route.params.id}/pdf/`,
      { responseType: 'blob' }
    )
    const url = URL.createObjectURL(
      new Blob([res.data], { type: 'application/pdf' })
    )
    window.open(url, '_blank')
  } catch (e) {
    ElMessage.error('PDF 생성에 실패했습니다.')
  } finally {
    pdfLoading.value = false
  }
}
</script>

<style scoped>
.print-container {
  width: 100%;
  max-width: 900px;
  margin: auto;
  background: #fff;
  padding: 24px;
  box-sizing: border-box;
  font-family: 'Noto Sans KR', sans-serif;
  color: #111;
  font-size: 13px;
  line-height: 1.5;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
  text-align: center;
}
.exp-ref { margin-left: 6px; vertical-align: 1px; }
.doc-no {
  font-size: 13px;
  font-weight: normal;
  color: #909399;
  margin-top: 4px;
}
.approval-hint {
  font-size: 13px;
  font-weight: normal;
  color: #e6a23c;
  margin-left: 8px;
}
.no-receive-hint {
  font-size: 12px;
  color: #c0c4cc;
}

.approval-table {
  width: 100%;
  margin-top: 12px;
  border-collapse: collapse;
  font-size: 13px;
}
.approval-table th,
.approval-table td {
  border: 1px solid #333;
  padding: 8px;
  text-align: center;
}
.sign-cell {
  height: 60px;
}

.info-table {
  width: 100%;
  margin-top: 24px;
  border-collapse: collapse;
  font-size: 13px;
}
.info-table td {
  padding: 8px;
  border: 1px solid #ccc;
}
.label-cell {
  width: 120px;
  background-color: #f8f8f8;
  font-weight: bold;
  text-align: center;
}

.receiving-summary {
  margin-top: 20px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
}
.receiving-summary-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}
.receiving-summary-value {
  font-weight: normal;
  color: #909399;
}

.item-table {
  width: 100%;
  margin-top: 24px;
  border-collapse: collapse;
  font-size: 13px;
}
.item-table th,
.item-table td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: center;
}
.item-table th {
  background-color: #f3f3f3;
  font-weight: bold;
}
.right-cell {
  text-align: right;
  font-weight: bold;
  background-color: #f9f9f9;
}

.item-name-row {
  font-size: 13px;
  padding: 10px;
  margin-top: 12px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
}

.button-group {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 12px;
}
.swatch-cell {
  width: 50mm;
  height: 50mm;
  border: 1px solid #999;
  background-color: #fff;
}
.print-only {
  display: none;
}
.progress-text {
  font-size: 11px;
  color: #606266;
  margin-left: 4px;
}
</style>
